"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import type {
  CandidateEditableProfileDto,
  CandidateSessionResponseDto,
  JobItemDto,
  ResumeUploadSource,
} from "@artha/api-contracts";
import {
  RESUME_UPLOAD_MAX_BYTES,
  RESUME_UPLOAD_MIME_TYPES,
  type ResumeUploadMimeType,
} from "@artha/api-contracts";
import {
  clearCandidateIdentity,
  readCandidateIdentity,
  writeCandidateIdentity,
  type CandidateIdentityStorage,
} from "../services/storage";
import {
  fetchCandidateSession,
  logoutCandidate,
  requestCandidateVerification,
  uploadResumeForPersonalization,
  verifyCandidateCode,
} from "../services/personalizationClient";
import { isResumePersonalizationUiEnabled } from "../personalization.constants";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { fetchPublicJobs } from "@/modules/common/services/jobApi";

type VerificationMode = "profile" | "login";

const hasPersonalizedProfile = (
  profile: CandidateEditableProfileDto | null | undefined,
) =>
  Boolean(
    profile &&
      (profile.titles.length > 0 ||
        profile.skills.length > 0 ||
        profile.keywords.length > 0),
  );

export const useCandidatePersonalization = (creatorId?: string) => {
  const t = useTranslations("profile.resumePersonalization");
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  useEffect(() => {
    posthogRef.current = posthog;
  }, [posthog]);
  const [identity, setIdentity] = useState<CandidateIdentityStorage>(
    readCandidateIdentity,
  );
  const [editableProfile, setEditableProfile] =
    useState<CandidateEditableProfileDto | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(
    isResumePersonalizationUiEnabled,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [verificationMode, setVerificationMode] =
    useState<VerificationMode>("profile");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [jobsRefreshVersion, setJobsRefreshVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const capturePersonalizationEvent = useCallback(
    (
      event: string,
      properties: Record<string, string | boolean | undefined> = {},
    ) => {
      posthogRef.current?.capture?.(event, {
        ...(creatorId && { portal_id: creatorId }),
        ...properties,
      });
    },
    [creatorId],
  );

  const applySession = useCallback(
    (session: CandidateSessionResponseDto, refreshJobs: boolean) => {
      if (!session.authenticated || !session.externalId) return;
      const isPersonalized =
        session.hasResume || hasPersonalizedProfile(session.editableProfile);
      const nextIdentity: CandidateIdentityStorage = {
        externalId: session.externalId,
        resumePersonalized: isPersonalized,
        candidatePreferences: session.preferences ?? null,
      };
      setIdentity(nextIdentity);
      writeCandidateIdentity(nextIdentity);
      setEditableProfile(session.editableProfile ?? null);
      setHasResume(session.hasResume);
      setIsAuthenticated(true);
      if (refreshJobs && isPersonalized) {
        setJobsRefreshVersion((version) => version + 1);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isResumePersonalizationUiEnabled) return;

    let active = true;
    void fetchCandidateSession()
      .then((session) => {
        if (!active) return;
        if (session.authenticated) {
          applySession(session, true);
        } else {
          setIdentity({
            externalId: null,
            resumePersonalized: false,
            candidatePreferences: null,
          });
          clearCandidateIdentity();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsSessionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applySession]);

  const getPosthogDistinctId = useCallback(() => {
    const distinctId = posthog?.get_distinct_id?.();
    if (!distinctId) {
      throw new Error(t("errors.identityNotReady"));
    }
    return distinctId;
  }, [posthog, t]);

  const uploadResume = useCallback(
    async (
      file: File,
      source: ResumeUploadSource = "jobs_page",
      job?: Pick<JobItemDto, "id" | "ref_id">,
    ) => {
      if (!creatorId) throw new Error(t("errors.creatorUnavailable"));
      if (
        !RESUME_UPLOAD_MIME_TYPES.includes(file.type as ResumeUploadMimeType) ||
        file.size > RESUME_UPLOAD_MAX_BYTES
      ) {
        const message = t("errors.unsupportedResume");
        setError(message);
        throw new Error(message);
      }

      setIsUploading(true);
      setError(null);
      try {
        const result = await uploadResumeForPersonalization({
          file,
          creatorId,
          posthogDistinctId: getPosthogDistinctId(),
          externalId: identity.externalId || undefined,
          source,
          jobId: job?.id,
          refId: job?.ref_id || undefined,
        });
        if (result.parserStatus !== "success" || !result.editableProfile) {
          throw new Error(t("errors.resumeUnreadable"));
        }
        const nextIdentity = {
          ...identity,
          externalId: result.externalId,
        };
        setIdentity(nextIdentity);
        writeCandidateIdentity(nextIdentity);
        setEditableProfile(result.editableProfile);
        setHasResume(true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_UPLOAD, {
          file_type: file.type,
          replacing_resume: hasResume,
          source,
        });
        return result;
      } catch (uploadError) {
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : t("errors.resumeUploadFailed");
        setError(message);
        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [
      capturePersonalizationEvent,
      creatorId,
      getPosthogDistinctId,
      hasResume,
      identity,
      t,
    ],
  );

  const requestProfileVerificationResult = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      options?: {
        source?: ResumeUploadSource;
        creatorId?: string;
        job?: Pick<JobItemDto, "id" | "ref_id">;
        fitAttemptId?: string;
      },
    ) => {
      if (!identity.externalId) {
        throw new Error(t("errors.uploadFirst"));
      }
      setIsRequestingCode(true);
      setError(null);
      try {
        const result = await requestCandidateVerification({
          externalId: identity.externalId,
          posthogDistinctId: getPosthogDistinctId(),
          editableProfile: profile,
          creatorId: options?.creatorId ?? creatorId ?? undefined,
          source: options?.source,
          jobId: options?.job?.id,
          refId: options?.job?.ref_id || undefined,
          fitAttemptId: options?.fitAttemptId,
        });
        setEditableProfile(profile);
        setVerificationMode("profile");
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
        }
        if (result.applyGate?.status === "no_match") {
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_JOB_FIT_NO_MATCH, {
            job_id: options?.job?.id,
            ref_id: options?.job?.ref_id || undefined,
          });
        }
        return result;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [
      applySession,
      capturePersonalizationEvent,
      creatorId,
      getPosthogDistinctId,
      identity.externalId,
      t,
    ],
  );

  const requestProfileVerification = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      options?: { source?: ResumeUploadSource; creatorId?: string },
    ) =>
      (await requestProfileVerificationResult(profile, options))
        .verificationRequired,
    [requestProfileVerificationResult],
  );

  const requestSignIn = useCallback(
    async (
      email: string,
      options?: { source?: ResumeUploadSource; creatorId?: string },
    ) => {
      setIsRequestingCode(true);
      setError(null);
      try {
        const result = await requestCandidateVerification({
          email,
          creatorId: options?.creatorId ?? creatorId ?? undefined,
          source: options?.source,
        });
        setVerificationMode("login");
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGIN, {
              has_resume: result.hasResume,
              email: (result.editableProfile?.email || email)
                .trim()
                .toLowerCase(),
          });
        }
        return result;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [applySession, capturePersonalizationEvent, creatorId, t],
  );

  const verifyCode = useCallback(
    async (email: string, code: string, fitAttemptId?: string) => {
      setIsVerifying(true);
      setError(null);
      try {
        const posthogDistinctId = getPosthogDistinctId();
        const result = await verifyCandidateCode({
          email,
          code,
          posthogDistinctId,
          ...(verificationMode === "profile" && identity.externalId
            ? {
                externalId: identity.externalId,
              }
            : {}),
          ...(fitAttemptId ? { fitAttemptId } : {}),
        });
        applySession(result, true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, {
          mode: verificationMode,
          has_resume: result.hasResume,
        });
        if (verificationMode === "login") {
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGIN, {
              has_resume: result.hasResume,
              email: (result.editableProfile?.email || email)
                .trim()
                .toLowerCase(),
          });
        }
        return result;
      } catch (verificationError) {
        const message =
          verificationError instanceof Error
            ? verificationError.message
            : t("errors.verificationFailed");
        setError(message);
        throw verificationError;
      } finally {
        setIsVerifying(false);
      }
    },
    [
      applySession,
      capturePersonalizationEvent,
      getPosthogDistinctId,
      identity.externalId,
      t,
      verificationMode,
    ],
  );

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setError(null);
    try {
      await logoutCandidate();
      capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGOUT, {
        ...(editableProfile?.email && {
          email: editableProfile.email.trim().toLowerCase(),
        }),
      });
      clearCandidateIdentity();
      setIdentity({
        externalId: null,
        resumePersonalized: false,
        candidatePreferences: null,
      });
      setEditableProfile(null);
      setHasResume(false);
      setIsAuthenticated(false);
      setMaskedEmail("");
      posthog?.reset?.();
      setJobsRefreshVersion((version) => version + 1);
    } catch (logoutError) {
      const message =
        logoutError instanceof Error
          ? logoutError.message
          : t("errors.logoutFailed");
      setError(message);
      throw logoutError;
    } finally {
      setIsLoggingOut(false);
    }
  }, [capturePersonalizationEvent, editableProfile, posthog, t]);

  const getAlternativeJobs = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      currentJob: Pick<JobItemDto, "id" | "ref_id" | "country">,
    ) => {
      const result = await fetchPublicJobs(
        "",
        {
          limit: 6,
          offset: 0,
          location: profile.location?.country || currentJob.country || "US",
        },
        {
          baseUrl: "/api/job-api",
          candidatePreferences: {
            titles: profile.titles,
            skills: profile.skills,
            keywords: profile.keywords,
          },
          creator: creatorId ? { id: creatorId } : undefined,
          visitorId: getPosthogDistinctId(),
          pulseVariant: "control",
        },
      );
      return result.items
        .filter(
          (job) =>
            job.id !== currentJob.id &&
            (!currentJob.ref_id || job.ref_id !== currentJob.ref_id),
        )
        .slice(0, 5);
    },
    [creatorId, getPosthogDistinctId],
  );

  return {
    externalId: isAuthenticated ? identity.externalId : null,
    isAuthenticated,
    isSessionLoading,
    isPersonalized:
      isAuthenticated &&
      (hasResume || hasPersonalizedProfile(editableProfile)),
    hasResume,
    candidatePreferences: isAuthenticated
      ? identity.candidatePreferences
      : null,
    editableProfile,
    maskedEmail,
    verificationMode,
    isUploading,
    isRequestingCode,
    isVerifying,
    isLoggingOut,
    jobsRefreshVersion,
    error,
    uploadResume,
    requestProfileVerification,
    requestApplyFitVerification: requestProfileVerificationResult,
    requestSignIn,
    verifyCode,
    getAlternativeJobs,
    logout,
  };
};

export type CandidatePersonalizationController = ReturnType<
  typeof useCandidatePersonalization
>;
